const BLOCKED_FRAME_ERROR_FRAGMENT = 'Blocked a frame with origin'
const SECURITY_POLICY_ERROR_FRAGMENT = 'security policy of the user agent'
const SECURITY_ERROR_TYPE = 'SecurityError'
const DOM_EXCEPTION_TYPE = 'DOMException'
const ERROR_TYPE = 'Error'
const SDK_ERROR_TYPE = 'SdkError'
const ONFIDO_STACKTRACE_MARKER = 'Onfido.js'
const POSTHOG_STACKTRACE_MARKER = 'posthog-js'
const POSTHOG_RECORDER_LOAD_RACE_MESSAGE = 'Called on script loaded before session recording is available'
const FETCH_REQUEST_FAILED_REGEX = /^Fetch request to (.+) failed$/

const RECORDER_STACKTRACE_MARKERS = ['lazy-loaded-session-recorder.ts', 'posthog-recorder.js']
const NOISY_ONFIDO_FETCH_TARGET_MARKERS = [
    'api.ca.onfido.com/v4/sdk/analytics',
    'api.ca.onfido.com/v4/sdk/logger',
    'api.ca.onfido.com/v4/analytics',
    'google.com/g/collect',
    'googleadservices.com',
    'doubleclick.net',
    'analytics.google.com'
]
const NOISY_ONFIDO_FETCH_TARGET_EXACT_MATCHES = ['undefined']

type SentryEventExceptionValue = {
    type?: string
    value?: string
    stacktrace?: {
        frames?: Array<{
            filename?: string
        }>
    }
}

type SentryEventLike = {
    message?: string
    exception?: {
        values?: SentryEventExceptionValue[]
    }
}

const hasCrossOriginSecurityMessage = (message?: string) =>
    Boolean(message?.includes(BLOCKED_FRAME_ERROR_FRAGMENT) || message?.includes(SECURITY_POLICY_ERROR_FRAGMENT))

const isReplayOrRecorderFrame = (filename?: string) => Boolean(filename && RECORDER_STACKTRACE_MARKERS.some((marker) => filename.includes(marker)))
const isOnfidoFrame = (filename?: string) => Boolean(filename?.includes(ONFIDO_STACKTRACE_MARKER))
const isPostHogFrame = (filename?: string) => Boolean(filename?.includes(POSTHOG_STACKTRACE_MARKER))
const isNoisyOnfidoFetchTarget = (target: string) =>
    NOISY_ONFIDO_FETCH_TARGET_EXACT_MATCHES.includes(target) || NOISY_ONFIDO_FETCH_TARGET_MARKERS.some((marker) => target.includes(marker))
const getFetchRequestTarget = (value?: string): string | undefined => value?.match(FETCH_REQUEST_FAILED_REGEX)?.[1]

export const isNoisyReplayCrossOriginSecurityError = (event: SentryEventLike): boolean => {
    const exceptionValues = event.exception?.values ?? []
    const hasSecurityErrorType = exceptionValues.some(
        (value) => value.type === SECURITY_ERROR_TYPE || value.type === DOM_EXCEPTION_TYPE || value.value?.startsWith(`${SECURITY_ERROR_TYPE}:`) === true
    )
    const hasSecurityMessage = hasCrossOriginSecurityMessage(event.message) || exceptionValues.some((value) => hasCrossOriginSecurityMessage(value.value))

    if (!hasSecurityErrorType || !hasSecurityMessage) {
        return false
    }

    return exceptionValues.some((value) => (value.stacktrace?.frames ?? []).some((frame) => isReplayOrRecorderFrame(frame.filename)))
}

export const isNoisyOnfidoSdkFetchError = (event: SentryEventLike): boolean => {
    const exceptionValues = event.exception?.values ?? []
    const fetchTargets = exceptionValues
        .filter((value) => value.type === SDK_ERROR_TYPE)
        .map((value) => getFetchRequestTarget(value.value))
        .filter((target): target is string => Boolean(target))

    if (fetchTargets.length === 0 || !fetchTargets.some(isNoisyOnfidoFetchTarget)) {
        return false
    }

    return exceptionValues.some((value) => (value.stacktrace?.frames ?? []).some((frame) => isOnfidoFrame(frame.filename)))
}

export const isNoisyPostHogRecorderLoadRaceError = (event: SentryEventLike): boolean => {
    const exceptionValues = event.exception?.values ?? []
    const hasRecorderLoadRaceMessage =
        event.message === POSTHOG_RECORDER_LOAD_RACE_MESSAGE ||
        exceptionValues.some((value) => value.type === ERROR_TYPE && value.value === POSTHOG_RECORDER_LOAD_RACE_MESSAGE)

    if (!hasRecorderLoadRaceMessage) {
        return false
    }

    return exceptionValues.some((value) => (value.stacktrace?.frames ?? []).some((frame) => isPostHogFrame(frame.filename)))
}
